/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useId } from 'react';
import { Ban, Calendar, Check, ChevronDown, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { RadioGroupItem } from '@/components/forms/radio-group';
import { Label } from '@/components/forms/label';
import { Typography } from '@/components/typography';
import { cn } from '@/lib/utils';

/** Leading control rendered next to the method title. */
export type DeliveryMethodTileControl = 'radio' | 'dot' | 'none';

/** Status icon shown beside the method description. */
export type DeliveryMethodTileStatus = 'included' | 'excluded' | 'info';

export interface RadioDeliveryMethodTileProps {
    /** Radio value submitted when chosen (only used when `control="radio"`). */
    value?: string;
    /** Visible method title, e.g. "White Glove Delivery". */
    title: string;
    /** Pre-formatted price string, e.g. "$99.99" or "FREE". Omitted when nullish. */
    price?: string | null;
    /** Supporting copy shown beside the status icon. */
    description: string;
    /**
     * Leading control rendered next to the title:
     *  • `'radio'` (default) — interactive `RadioGroupItem`; MUST be inside a `<RadioGroup>`.
     *  • `'dot'`             — static filled dot (locked-in choice); no `RadioGroup` needed.
     *  • `'none'`            — no control (plain method card); no `RadioGroup` needed.
     */
    control?: DeliveryMethodTileControl;
    /**
     * Status icon shown beside the description:
     *  • `'included'` (default) — green check.
     *  • `'excluded'`           — red prohibited icon.
     *  • `'info'`               — neutral info icon (informational / standard methods).
     */
    status?: DeliveryMethodTileStatus;
    /** Highlights the tile border to indicate the current selection. */
    selected?: boolean;
    /** Pre-formatted "Get it by" date, e.g. "Friday, April 6". Hidden when absent. */
    deliveryDate?: string | null;
    /**
     * When `true`, render the schedule row (calendar icon + "Change date"
     * button) even if `deliveryDate` is absent. Use this when the ZIP
     * supports OHDS scheduling but a date hasn't been auto-populated yet
     * (e.g., first eligible mount before the auto-populate PATCH lands,
     * or a sandbox where windows aren't available so no date can be
     * stamped). The row is hidden when both `deliveryDate` and
     * `showChangeDate` are falsy. Independent of `deliveryDate` so the
     * "Get it by …" text can appear or not appear without affecting the
     * Change date affordance.
     */
    showChangeDate?: boolean;
    /** Hide the date row ("Get it by …") entirely. */
    hideScheduleRow?: boolean;
    /** Disables the interactive radio (only relevant when `control="radio"`). */
    disabled?: boolean;
    /** Hide the "Delivery details / Free details" footer band. */
    hideFooter?: boolean;
    /** Invoked when the shopper clicks "Change date" (rendered whenever `deliveryDate` OR `showChangeDate` is truthy). */
    onChangeDate?: () => void;
    /** Invoked when the "Delivery details" footer link is clicked. */
    onDeliveryDetails?: () => void;
    /** Invoked when the "Free details" footer link is clicked. */
    onFreeDetails?: () => void;
    /** Controls visibility of the "Delivery details" action in footer. */
    showDeliveryDetails?: boolean;
    /** Controls visibility of the "Free details" action in footer. */
    showFreeDetails?: boolean;
    /** Optional explicit id (used to associate the radio with its label). */
    id?: string;
    /** Explicit test id for the inner radio control (defaults to `${data-testid}-radio`). */
    radioTestId?: string;
    /** Explicit test id for the price element. */
    priceTestId?: string;
    className?: string;
    'data-testid'?: string;
}

/**
 * Delivery-method radio tile — the full delivery-option card from Figma
 * (node 34613-66829). Presentational only: callers own selection state (wrap
 * the tiles in a `RadioGroup`) and the data passed in via props.
 *
 * Anatomy (top → bottom):
 *   1. Control (`radio` / `dot` / `none`) + method title + optional price.
 *   2. Optional "Get it by {date} | Change date" row (shown when `deliveryDate`
 *      is provided; "Change date" invokes `onChangeDate` when clicked).
 *   3. Status row — check (`included`) / prohibited (`excluded`) / info — + description.
 *   4. "Delivery details / Free details" footer band (suppressed via `hideFooter`).
 *
 * @example
 * // Interactive — inside a RadioGroup
 * <RadioGroup value={value} onValueChange={setValue}>
 *   <RadioDeliveryMethodTile
 *     control="radio"
 *     value="HD"
 *     title={t('shipping.method.whiteGlove')}
 *     price="$99.99"
 *     description={t('shipping.method.whiteGloveDescription')}
 *     selected={value === 'HD'}
 *     deliveryDate="Friday, April 6"
 *   />
 * </RadioGroup>
 *
 * @example
 * // Standalone — no RadioGroup needed
 * <RadioDeliveryMethodTile
 *   control="none"
 *   status="info"
 *   title={t('shipping.method.groundShipping')}
 *   price="FREE"
 *   description={t('shipping.method.groundShippingDescription')}
 * />
 */
export default function RadioDeliveryMethodTile({
    value,
    title,
    price,
    description,
    control = 'radio',
    status = 'included',
    selected = false,
    deliveryDate,
    showChangeDate = false,
    hideScheduleRow = false,
    disabled = false,
    hideFooter = false,
    onChangeDate,
    onDeliveryDetails,
    onFreeDetails,
    showDeliveryDetails = true,
    showFreeDetails = true,
    id,
    radioTestId,
    priceTestId,
    className,
    'data-testid': dataTestId,
}: RadioDeliveryMethodTileProps) {
    const { t } = useTranslation('checkout');
    const generatedId = useId();
    const radioId = id ?? generatedId;
    const hasBothFooterActions = showDeliveryDetails && showFreeDetails;

    return (
        <div
            className={cn(
                'flex flex-col overflow-hidden rounded-xs border transition-colors',
                selected ? 'border-border' : 'border-border',
                className
            )}
            data-testid={dataTestId}>
            {/* Radio / dot + title + price */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
                <span className="flex min-w-0 items-center gap-3">
                    {control === 'radio' ? (
                        <>
                            <RadioGroupItem
                                value={value ?? title}
                                id={radioId}
                                disabled={disabled}
                                className="shrink-0"
                                data-testid={radioTestId ?? (dataTestId ? `${dataTestId}-radio` : undefined)}
                            />
                            <Label
                                htmlFor={radioId}
                                className="cursor-pointer truncate text-base font-semibold leading-tight text-foreground">
                                {title}
                            </Label>
                        </>
                    ) : (
                        <>
                            {control === 'dot' && (
                                <span
                                    aria-hidden="true"
                                    className="inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-foreground">
                                    <span className="size-2 rounded-full bg-foreground" />
                                </span>
                            )}
                            <Typography
                                variant="p"
                                className="truncate text-base font-semibold leading-tight text-foreground">
                                {title}
                            </Typography>
                        </>
                    )}
                </span>
                {price && (
                    <span className="shrink-0 text-base leading-tight text-foreground" data-testid={priceTestId}>
                        {price}
                    </span>
                )}
            </div>

            {/*
             * Schedule row — renders when EITHER:
             *  • `deliveryDate` is set → "Get it by {date} | Change date"
             *  • `showChangeDate` is `true` (eligible ZIP, no committed
             *    date yet) → just "Change date"
             * The Change date affordance is anchored to ZIP eligibility,
             * not to the presence of a committed date — clicking opens
             * the popover whose body adapts to the available data
             * (calendar when windows are present, empty-state otherwise).
             */}
            {!hideScheduleRow && (deliveryDate || showChangeDate) && (
                <div className="flex flex-wrap items-center gap-2 px-4 pt-3 text-sm text-muted-foreground">
                    <Calendar className="size-4 shrink-0" aria-hidden="true" />
                    {deliveryDate && (
                        <>
                            <span>
                                {t('shipping.getItBy')}{' '}
                                <span className="font-semibold text-foreground">{deliveryDate}</span>
                            </span>
                            {showChangeDate && (
                                <span aria-hidden="true" className="text-border">
                                    |
                                </span>
                            )}
                        </>
                    )}
                    {showChangeDate && (
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onChangeDate}
                            className="h-auto gap-1 p-0 normal-case tracking-normal text-sm font-normal text-muted-foreground hover:bg-transparent hover:text-foreground">
                            {t('shipping.changeDate')}
                            <ChevronDown className="size-4" aria-hidden="true" />
                        </Button>
                    )}
                </div>
            )}

            {/* Status icon + description */}
            <div className="flex items-start gap-2 px-4 py-3">
                {status === 'included' && <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />}
                {status === 'excluded' && (
                    <Ban className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
                )}
                {status === 'info' && (
                    <Info className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                )}
                <Typography variant="small" className="text-sm leading-5 text-muted-foreground">
                    {description}
                </Typography>
            </div>

            {/* Footer detail band */}
            {!hideFooter && (
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 border-t border-border bg-secondary px-4 py-3">
                    {showDeliveryDetails && (
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onDeliveryDetails}
                            className={cn(
                                'h-auto min-w-fit p-0 text-left sm:text-center hover:bg-transparent hover:text-muted-foreground',
                                hasBothFooterActions && 'xs:flex-1 sm:flex-none'
                            )}>
                            {t('shipping.deliveryDetails')}
                        </Button>
                    )}
                    {showFreeDetails && (
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onFreeDetails}
                            className={cn(
                                'h-auto min-w-fit p-0 text-left sm:text-center hover:bg-transparent hover:text-muted-foreground',
                                hasBothFooterActions && 'xs:flex-1 sm:flex-none'
                            )}>
                            {t('shipping.freeDetails')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}
